import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Zap, Target, TrendingUp, Dumbbell, Heart, Brain } from 'lucide-react';
import * as api from '../lib/api';
import { COVERS } from '../lib/media';
import { VIDEOS } from '../lib/media';
import HeroVideo from '../components/HeroVideo';

export default function Performance() {
  const [book, setBook] = useState<api.BookListItem | undefined>(undefined);

  useEffect(() => {
    api.books.list().then(list => {
      setBook(list.find(b => b.slug === 'fasting-carnivore-performance' || b.title?.toLowerCase().includes('performance')));
    }).catch(() => {});
  }, []);

  const coverSrc = book?.coverUrl || COVERS.performance;
  return (
    <>
      {/* Hero */}
      <section className="hero">
        <HeroVideo className="video-bg" src={VIDEOS.meatSteak} />
        <div className="video-overlay" />
        <div className="container book-page-hero-container">
          <motion.div
            className="hero-content text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="hero-label">Book Three</span>
            <h1>Fasting Carnivore Performance</h1>
            <p>Train Harder. Recover Faster. Perform at Your Peak.</p>
            <Link to="/join" className="btn btn-primary btn-lg">
              Join the Community
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Pain Point */}
      <section className="section">
        <div className="container">
          <div className="content-narrow">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="heading-section text-center">
                You've Been Told You Need Carbs
              </h2>
              <div className="text-body-lg">
                <p className="mb-responsive-lg">
                  Every trainer, every coach, every nutrition "expert" has told you the same thing: carbs are essential for performance. It's the unquestioned gospel of sports nutrition.
                </p>
                <p className="mb-responsive-lg">
                  You need them for energy. You need them for recovery. You need them to build muscle. Without carbs, you'll bonk, lose strength, and fall apart.
                </p>
                <p className="mb-responsive-lg book-page-emphasis">
                  But what if that's not the whole story? What if there's another way—one that a growing number of elite athletes are discovering?
                </p>
                <p className="book-page-emphasis-accent">
                  What if you could perform at your peak while eating the foods your body was designed to thrive on—and never worry about carb timing, gel packs, or mid-workout fueling again?
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* The Truth */}
      <section className="section section-light">
        <div className="container">
          <div className="content-narrow">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="heading-section text-center">
                Fat-Adapted Athletes Are Rewriting the Rules
              </h2>
              <div className="text-body-lg">
                <p className="mb-responsive-lg">
                  Endurance athletes running ultramarathons without bonking. Powerlifters hitting PRs while fasted. CrossFitters crushing WODs on zero carbs. Fighters making weight without losing strength.
                </p>
                <p className="mb-responsive-lg">
                  They've discovered something the mainstream hasn't caught onto yet: when you become fat-adapted, you unlock a fuel source that never runs out. You stop being dependent on your next meal for energy.
                </p>
                <p className="book-page-emphasis">
                  No more energy crashes. No more mid-workout bonks. No more fueling strategies that require a PhD to understand. Just steady, sustained power—whenever you need it.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2>What You'll Discover</h2>
          </div>
          <div className="grid-3">
            <BenefitCard
              icon={<Zap size={32} />}
              title="Unlimited Energy"
              description="Access your body's fat stores for steady, crash-free energy during any workout."
            />
            <BenefitCard
              icon={<Dumbbell size={32} />}
              title="Build Lean Muscle"
              description="Protein synthesis optimized. Recovery accelerated. Gains that stick."
            />
            <BenefitCard
              icon={<Heart size={32} />}
              title="Faster Recovery"
              description="Reduced inflammation means you bounce back faster between sessions."
            />
            <BenefitCard
              icon={<Target size={32} />}
              title="Mental Clarity"
              description="No brain fog. No afternoon crashes. Just sharp focus when you need it most."
            />
            <BenefitCard
              icon={<TrendingUp size={32} />}
              title="Body Composition"
              description="Burn fat while preserving muscle. The holy grail of athletic physique."
            />
            <BenefitCard
              icon={<Brain size={32} />}
              title="Metabolic Flexibility"
              description="Train your body to use any fuel source efficiently. Become unstoppable."
            />
          </div>
        </div>
      </section>

      {/* The Book */}
      <section className="section section-light">
        <div className="container">
          <motion.div
            className="book-page-section-intro"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="section-label">The Book</span>
            <h2 className="heading-section">Fasting Carnivore Performance</h2>
          </motion.div>
          <div className="two-col book-page-two-col">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <img
                src={coverSrc}
                alt="Fasting Carnivore Performance Book"
                className="book-page-cover"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="book-page-feature-heading">
                The Complete Athletic Guide
              </h3>
              <p className="text-body-lg book-page-body-spaced">
                From strength training to endurance, pre-workout nutrition to recovery protocols—everything you need to optimize your performance on a meat-based diet. This is the playbook that carb-dependent athletes don't have.
              </p>
              <ul className="book-page-feature-list">
                <li>10 comprehensive chapters covering every aspect of athletic performance</li>
                <li>Training protocols for strength, endurance, and everything in between</li>
                <li>Pre and post-workout strategies that actually work without carbs</li>
                <li>Body composition optimization—build muscle while staying lean</li>
                <li>Mental performance techniques for focus and drive</li>
                <li>Troubleshooting common challenges athletes face on carnivore</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="section">
        <div className="container">
          <div className="content-narrow">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="heading-section text-center">
                This Is For You If...
              </h2>
              <div className="text-body-lg">
                <ul className="book-page-list-spaced">
                  <li>You want to perform at your best without being dependent on carbs for fuel</li>
                  <li>You're tired of energy crashes, bonking mid-workout, and constantly needing to refuel</li>
                  <li>You want to build lean muscle without gaining unwanted fat</li>
                  <li>You're curious about fasted training and want to understand how it works</li>
                  <li>You want to become metabolically flexible—able to perform no matter what you've eaten</li>
                  <li>You're ready to question conventional sports nutrition and try what's actually working for elite athletes</li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta">
        <div className="container text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2>Ready to Transform Your Performance?</h2>
            <p>Get instant access to all four books, workbooks, and a community of athletes who are doing this together.</p>
            <Link to="/join" className="btn btn-primary btn-lg">
              Join the Community — $97 Lifetime
            </Link>
            <p className="book-page-cta-fine-subtle">
              All books included · Lifetime access · No recurring fees
            </p>
          </motion.div>
        </div>
      </section>
    </>
  );
}

function BenefitCard({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) {
  return (
    <motion.div
      className="book-page-icon-card"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
    >
      <div className="book-page-icon-card-icon">{icon}</div>
      <h3 className="book-page-icon-card-title">{title}</h3>
      <p className="book-page-icon-card-desc">{description}</p>
    </motion.div>
  );
}
